var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable max-lines-per-function */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import cn from 'classnames';
import { useUID } from 'react-uid';
export const InputStepper = forwardRef((_a, ref) => {
    var { step = 1, min, max, digits = 2, validationMessageId, label, labelId, ariaLabel, labelClassName, name, condensed = false, disabled = false, increaseButtonDisabled = false, decreaseButtonDisabled = false, required, validation, validationMsg, validationMsgRole, helperMsg, helperClassName, ariaLive, ariaDescribedby, mainClassName, onValueChange = () => { }, onValidation = () => { }, onChange = () => { }, onFocus = () => { }, onDecreaseClick = () => { }, onIncreaseClick = () => { }, onBlur = () => { }, onKeyUp = () => { }, onKeyDown = () => { }, value } = _a, props = __rest(_a, ["step", "min", "max", "digits", "validationMessageId", "label", "labelId", "ariaLabel", "labelClassName", "name", "condensed", "disabled", "increaseButtonDisabled", "decreaseButtonDisabled", "required", "validation", "validationMsg", "validationMsgRole", "helperMsg", "helperClassName", "ariaLive", "ariaDescribedby", "mainClassName", "onValueChange", "onValidation", "onChange", "onFocus", "onDecreaseClick", "onIncreaseClick", "onBlur", "onKeyUp", "onKeyDown", "value"]);
    const uid = useUID();
    const inputId = props.id || `apricot_input_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_input_err_${uid}`;
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);
    const [currentStepValue, setCurrentStepValue] = useState(value || min);
    const [validationState, setValidationState] = useState(validation);
    const errorMsg = validationMsg || `Please enter a value between ${min} and ${max}`;
    const [validationMessage, setValidationMessage] = useState(errorMsg);
    const mainClass = cn('cb-input', 'cb-stepper-input', disabled && 'cb-disabled', condensed && 'cb-condensed', validationState && `cb-validation-${validationState}`, digits && digits > 2 ? `cb-stepper-input-${digits <= 4 ? digits : 'unset'}` : null, mainClassName);
    const labelClass = cn(required && 'cb-required', condensed && 'cb-condensed', disabled && 'cb-disabled', labelClassName);
    const helperClass = cn('cb-input-helper', validationState && `cb-validation-${validationState}`, helperClassName);
    const isNumber = (n) => {
        if (n === '' || n === undefined)
            return false;
        const v = Number(n);
        return typeof v === 'number' && Number.isFinite(v);
    };
    const validateInput = (val) => {
        if (isNumber(min) && isNumber(max)) {
            if (val < Number(min) || val > Number(max)) {
                setValidationState('error');
                setValidationMessage(errorMsg);
                onValidation && onValidation(val);
            }
            else {
                setValidationState(undefined);
            }
        }
    };
    const stepDecrease = (e) => {
        setCurrentStepValue((prev) => {
            const newValue = Number(prev) - step;
            isNumber(newValue) && validateInput(newValue);
            // if (min && newValue <= min) newValue = min;
            onValueChange && onValueChange(newValue);
            return newValue;
        });
        onDecreaseClick && onDecreaseClick(e);
    };
    const stepIncrease = (e) => {
        setCurrentStepValue((prev) => {
            const newValue = Number(prev) + step;
            isNumber(newValue) && validateInput(newValue);
            onValueChange && onValueChange(newValue);
            return newValue;
        });
        onIncreaseClick && onIncreaseClick(e);
    };
    const handleInputChange = (e) => {
        setCurrentStepValue(e.target.value);
        onChange && onChange(e);
        onValueChange && onValueChange(Number(e.target.value));
    };
    const handleInputBlur = (e) => {
        if (isNumber(e.target.value) && isNumber(min) && isNumber(max)) {
            validateInput(e.target.value);
        }
        onBlur && onBlur(e);
    };
    const getDescribedBy = () => {
        if (validationState) {
            if (validationMessage && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMessage ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    useEffect(() => {
        setValidationMessage(validationMsg);
    }, [validationMsg]);
    useEffect(() => {
        setValidationState(validation);
    }, [validation]);
    useEffect(() => {
        if (value !== undefined) {
            setCurrentStepValue(value);
            if (isNumber(min) && isNumber(max)) {
                value && validateInput(value);
            }
        }
    }, [value]);
    return (React.createElement(React.Fragment, null,
        !!label && (React.createElement("label", { htmlFor: inputId, className: labelClass, id: labelId }, label)),
        React.createElement("div", { className: mainClass, "data-cbtrack-comp": "apricot-react:stepper-input" },
            React.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-greyscale cb-stepper-decrease", "aria-controls": inputId, onClick: stepDecrease, disabled: disabled ||
                    decreaseButtonDisabled ||
                    (min !== undefined && currentStepValue ? currentStepValue < min + step : false) ||
                    currentStepValue === min },
                React.createElement("span", { className: "cb-icon cb-minus", "aria-hidden": "true" }),
                React.createElement("span", { className: "sr-only" },
                    "Decrease by ",
                    step)),
            React.createElement("input", Object.assign({ name: name, type: "number", "aria-live": "polite", id: inputId, ref: inputRef, value: currentStepValue, "aria-label": ariaLabel, onChange: handleInputChange, onFocus: onFocus, onBlur: handleInputBlur, onKeyUp: onKeyUp, onKeyDown: onKeyDown, "aria-describedby": describedbyValue || undefined, "aria-invalid": validation === 'error', disabled: disabled, min: min, max: max }, props)),
            React.createElement("button", { type: "button", className: "cb-btn cb-btn-square cb-btn-greyscale cb-stepper-increase", "aria-controls": inputId, onClick: stepIncrease, disabled: disabled ||
                    increaseButtonDisabled ||
                    (max !== undefined && currentStepValue ? currentStepValue > max - step : false) ||
                    currentStepValue === max },
                React.createElement("span", { className: "cb-icon cb-plus", "aria-hidden": "true" }),
                React.createElement("span", { className: "sr-only" },
                    "Increase by ",
                    step))),
        validationState && validationMessage && (React.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, validationMessage)),
        !validationState && (React.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive, role: validationMsgRole }, helperMsg))));
});
//# sourceMappingURL=InputStepper.js.map